/**
 * OutboundMessageModal — reusable picker for WhatsApp (and future) outbound channels.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { WhatsApp } from "@mui/icons-material";
import {
  getDefaultChannelId,
  openOutboundWhatsApp,
  setDefaultChannelId,
} from "../utils/outboundMessaging.js";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {'fixed'|'channel'} [props.recipientMode] - fixed: message to recipientPhone; channel: message to selected channel
 * @param {string} [props.recipientPhone] - Required when recipientMode is fixed
 * @param {string} [props.recipientLabel] - Shown when recipientMode is fixed
 * @param {() => Promise<Array>} props.loadChannels
 * @param {(channel: Object) => string} props.buildMessage
 * @param {string} [props.storageKey]
 * @param {string} [props.confirmLabel]
 * @param {string} [props.searchPlaceholder]
 * @param {string} [props.channelListLabel]
 * @param {string} [props.emptyChannelsMessage]
 * @param {(channel: Object) => void} [props.onOpened]
 */
const OutboundMessageModal = ({
  open,
  onClose,
  title,
  description,
  recipientMode = "fixed",
  recipientPhone,
  recipientLabel = "Customer",
  loadChannels,
  buildMessage,
  storageKey,
  confirmLabel = "Open WhatsApp",
  searchPlaceholder = "Search by name or phone",
  channelListLabel = "Reply as",
  emptyChannelsMessage = "No messaging channels found.",
  onOpened,
}) => {
  const [search, setSearch] = useState("");
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [editedMessage, setEditedMessage] = useState("");
  const [actionError, setActionError] = useState(null);

  const fetchChannels = useCallback(async () => {
    if (!loadChannels) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await loadChannels();
      setChannels(Array.isArray(result) ? result : []);
    } catch (error) {
      setChannels([]);
      setLoadError(error.message || "Failed to load channels");
    } finally {
      setLoading(false);
    }
  }, [loadChannels]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedChannel(null);
      setActionError(null);
      fetchChannels();
    }
  }, [open, fetchChannels]);

  useEffect(() => {
    if (!open || channels.length === 0) return;

    const savedId = storageKey ? getDefaultChannelId(storageKey) : null;
    const saved = savedId
      ? channels.find((channel) => channel.id === savedId)
      : null;
    setSelectedChannel(saved || channels[0] || null);
  }, [open, channels, storageKey]);

  const filteredChannels = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return channels;

    return channels.filter(
      (channel) =>
        channel.label?.toLowerCase().includes(term) ||
        channel.phone?.includes(search.trim()) ||
        channel.subtitle?.toLowerCase().includes(term)
    );
  }, [channels, search]);

  const generatedMessage = useMemo(() => {
    if (!selectedChannel || !buildMessage) return "";
    try {
      return buildMessage(selectedChannel) || "";
    } catch {
      return "";
    }
  }, [selectedChannel, buildMessage]);

  useEffect(() => {
    setEditedMessage(generatedMessage);
  }, [generatedMessage]);

  const handleOpen = () => {
    if (!selectedChannel) {
      setActionError("Select a messaging channel");
      return;
    }

    if (recipientMode === "fixed" && !recipientPhone) {
      setActionError("Customer phone number is missing");
      return;
    }

    try {
      const messageText = editedMessage.trim() || generatedMessage;
      openOutboundWhatsApp({
        recipientMode,
        recipientPhone,
        channel: selectedChannel,
        messageText,
      });

      if (storageKey) {
        setDefaultChannelId(storageKey, selectedChannel.id);
      }

      onOpened?.(selectedChannel);
      onClose();
    } catch (error) {
      setActionError(error.message || "Failed to open WhatsApp");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        {recipientMode === "fixed" && recipientPhone && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{recipientLabel}:</strong> {recipientPhone}
          </Typography>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {channelListLabel}
        </Typography>

        <TextField
          fullWidth
          label="Search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          autoFocus
        />

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 220,
            overflowY: "auto",
            mb: 2,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredChannels.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {channels.length === 0
                  ? emptyChannelsMessage
                  : "No channels match your search."}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredChannels.map((channel) => (
                <ListItemButton
                  key={channel.id}
                  selected={selectedChannel?.id === channel.id}
                  onClick={() => {
                    setSelectedChannel(channel);
                    setActionError(null);
                  }}
                >
                  <ListItemText
                    primary={channel.label}
                    secondary={channel.subtitle || channel.phone}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {generatedMessage && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Message preview
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={8}
              maxRows={14}
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              placeholder="Edit the message before sending"
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "0.8125rem",
                  lineHeight: 1.5,
                  bgcolor: "grey.50",
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<WhatsApp />}
          onClick={handleOpen}
          disabled={!selectedChannel || loading}
          sx={{
            backgroundColor: "#25D366",
            "&:hover": { backgroundColor: "#20BA5A" },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutboundMessageModal;
